var _Marked_instances, _Marked_parseMarkdown, _Marked_onError;
import { __classPrivateFieldGet } from "tslib";
import { _getDefaults } from './defaults.ts';
import { _Lexer } from './Lexer.ts';
import { _Parser } from './Parser.ts';
import { _Hooks } from './Hooks.ts';
import { _Renderer } from './Renderer.ts';
import { _Tokenizer } from './Tokenizer.ts';
import { _TextRenderer } from './TextRenderer.ts';
import { escape } from './helpers.ts';
export class Marked {
    constructor(...args) {
        _Marked_instances.add(this);
        this.defaults = _getDefaults();
        this.options = this.setOptions;
        this.parse = __classPrivateFieldGet(this, _Marked_instances, "m", _Marked_parseMarkdown).call(this, _Lexer.lex, _Parser.parse);
        this.parseInline = __classPrivateFieldGet(this, _Marked_instances, "m", _Marked_parseMarkdown).call(this, _Lexer.lexInline, _Parser.parseInline);
        this.Parser = _Parser;
        this.parser = _Parser.parse;
        this.Renderer = _Renderer;
        this.TextRenderer = _TextRenderer;
        this.Lexer = _Lexer;
        this.lexer = _Lexer.lex;
        this.Tokenizer = _Tokenizer;
        this.Hooks = _Hooks;
        this.use(...args);
    }
    /**
     * Run callback for every token
     */
    walkTokens(tokens, callback) {
        var _a, _b;
        let values = [];
        for (const token of tokens) {
            values = values.concat(callback.call(this, token));
            switch (token.type) {
                case 'table': {
                    const tableToken = token;
                    for (const cell of tableToken.header) {
                        values = values.concat(this.walkTokens(cell.tokens, callback));
                    }
                    for (const row of tableToken.rows) {
                        for (const cell of row) {
                            values = values.concat(this.walkTokens(cell.tokens, callback));
                        }
                    }
                    break;
                }
                case 'list': {
                    const listToken = token;
                    values = values.concat(this.walkTokens(listToken.items, callback));
                    break;
                }
                default: {
                    const genericToken = token;
                    if ((_b = (_a = this.defaults.extensions) === null || _a === void 0 ? void 0 : _a.childTokens) === null || _b === void 0 ? void 0 : _b[genericToken.type]) {
                        this.defaults.extensions.childTokens[genericToken.type].forEach((childTokens) => {
                            values = values.concat(this.walkTokens(genericToken[childTokens], callback));
                        });
                    }
                    else if (genericToken.tokens) {
                        values = values.concat(this.walkTokens(genericToken.tokens, callback));
                    }
                }
            }
        }
        return values;
    }
    use(...args) {
        const extensions = this.defaults.extensions || { renderers: {}, childTokens: {} };
        args.forEach((pack) => {
            // copy options to new object
            const opts = Object.assign({}, pack);
            // set async to true if it was set to true before
            opts.async = this.defaults.async || opts.async || false;
            // ==-- Parse "addon" extensions --== //
            if (pack.extensions) {
                pack.extensions.forEach((ext) => {
                    if (!ext.name) {
                        throw new Error('extension name required');
                    }
                    if ('renderer' in ext) { // Renderer extensions
                        const prevRenderer = extensions.renderers[ext.name];
                        if (prevRenderer) {
                            // Replace extension with func to run new extension but fall back if false
                            extensions.renderers[ext.name] = function (...args) {
                                let ret = ext.renderer.apply(this, args);
                                if (ret === false) {
                                    ret = prevRenderer.apply(this, args);
                                }
                                return ret;
                            };
                        }
                        else {
                            extensions.renderers[ext.name] = ext.renderer;
                        }
                    }
                    if ('tokenizer' in ext) { // Tokenizer Extensions
                        if (!ext.level || (ext.level !== 'block' && ext.level !== 'inline')) {
                            throw new Error("extension level must be 'block' or 'inline'");
                        }
                        const extLevel = extensions[ext.level];
                        if (extLevel) {
                            extLevel.unshift(ext.tokenizer);
                        }
                        else {
                            extensions[ext.level] = [ext.tokenizer];
                        }
                        if (ext.start) { // Function to check for start of token
                            if (ext.level === 'block') {
                                if (extensions.startBlock) {
                                    extensions.startBlock.push(ext.start);
                                }
                                else {
                                    extensions.startBlock = [ext.start];
                                }
                            }
                            else if (ext.level === 'inline') {
                                if (extensions.startInline) {
                                    extensions.startInline.push(ext.start);
                                }
                                else {
                                    extensions.startInline = [ext.start];
                                }
                            }
                        }
                    }
                    if ('childTokens' in ext && ext.childTokens) { // Child tokens to be visited by walkTokens
                        extensions.childTokens[ext.name] = ext.childTokens;
                    }
                });
                opts.extensions = extensions;
            }
            // ==-- Parse "overwrite" extensions --== //
            if (pack.renderer) {
                const renderer = this.defaults.renderer || new _Renderer(this.defaults);
                for (const prop in pack.renderer) {
                    const rendererFunc = pack.renderer[prop];
                    const rendererKey = prop;
                    const prevRenderer = renderer[rendererKey];
                    // Replace renderer with func to run extension, but fall back if false
                    renderer[rendererKey] = (...args) => {
                        let ret = rendererFunc.apply(renderer, args);
                        if (ret === false) {
                            ret = prevRenderer.apply(renderer, args);
                        }
                        return ret || '';
                    };
                }
                opts.renderer = renderer;
            }
            if (pack.tokenizer) {
                const tokenizer = this.defaults.tokenizer || new _Tokenizer(this.defaults);
                for (const prop in pack.tokenizer) {
                    const tokenizerFunc = pack.tokenizer[prop];
                    const tokenizerKey = prop;
                    const prevTokenizer = tokenizer[tokenizerKey];
                    // Replace tokenizer with func to run extension, but fall back if false
                    tokenizer[tokenizerKey] = (...args) => {
                        let ret = tokenizerFunc.apply(tokenizer, args);
                        if (ret === false) {
                            ret = prevTokenizer.apply(tokenizer, args);
                        }
                        return ret;
                    };
                }
                opts.tokenizer = tokenizer;
            }
            // ==-- Parse Hooks extensions --== //
            if (pack.hooks) {
                const hooks = this.defaults.hooks || new _Hooks();
                for (const prop in pack.hooks) {
                    const hooksFunc = pack.hooks[prop];
                    const hooksKey = prop;
                    const prevHook = hooks[hooksKey];
                    if (_Hooks.passThroughHooks.has(prop)) {
                        hooks[hooksKey] = (arg) => {
                            if (this.defaults.async) {
                                return Promise.resolve(hooksFunc.call(hooks, arg)).then(ret => {
                                    return prevHook.call(hooks, ret);
                                });
                            }
                            const ret = hooksFunc.call(hooks, arg);
                            return prevHook.call(hooks, ret);
                        };
                    }
                    else {
                        hooks[hooksKey] = (...args) => {
                            let ret = hooksFunc.apply(hooks, args);
                            if (ret === false) {
                                ret = prevHook.apply(hooks, args);
                            }
                            return ret;
                        };
                    }
                }
                opts.hooks = hooks;
            }
            // ==-- Parse WalkTokens extensions --== //
            if (pack.walkTokens) {
                const walkTokens = this.defaults.walkTokens;
                const packWalktokens = pack.walkTokens;
                opts.walkTokens = function (token) {
                    let values = [];
                    values.push(packWalktokens.call(this, token));
                    if (walkTokens) {
                        values = values.concat(walkTokens.call(this, token));
                    }
                    return values;
                };
            }
            this.defaults = Object.assign(Object.assign({}, this.defaults), opts);
        });
        return this;
    }
    setOptions(opt) {
        this.defaults = Object.assign(Object.assign({}, this.defaults), opt);
        return this;
    }
}
_Marked_instances = new WeakSet(), _Marked_parseMarkdown = function _Marked_parseMarkdown(lexer, parser) {
    return (src, options) => {
        const origOpt = Object.assign({}, options);
        const opt = Object.assign(Object.assign({}, this.defaults), origOpt);
        // Show warning if an extension set async to true but the parse was called with async: false
        if (this.defaults.async === true && origOpt.async === false) {
            if (!opt.silent) {
                console.warn('marked(): The async option was set to true by an extension. The async: false option sent to parse will be ignored.');
            }
            opt.async = true;
        }
        const throwError = __classPrivateFieldGet(this, _Marked_instances, "m", _Marked_onError).call(this, !!opt.silent, !!opt.async);
        // throw error in case of non string input
        if (typeof src === 'undefined' || src === null) {
            return throwError(new Error('marked(): input parameter is undefined or null'));
        }
        if (typeof src !== 'string') {
            return throwError(new Error('marked(): input parameter is of type '
                + Object.prototype.toString.call(src) + ', string expected'));
        }
        if (opt.hooks) {
            opt.hooks.options = opt;
        }
        if (opt.async) {
            return Promise.resolve(opt.hooks ? opt.hooks.preprocess(src) : src)
                .then(src => lexer(src, opt))
                .then(tokens => opt.walkTokens ? Promise.all(this.walkTokens(tokens, opt.walkTokens)).then(() => tokens) : tokens)
                .then(tokens => parser(tokens, opt))
                .then(html => opt.hooks ? opt.hooks.postprocess(html) : html)
                .catch(throwError);
        }
        try {
            if (opt.hooks) {
                src = opt.hooks.preprocess(src);
            }
            const tokens = lexer(src, opt);
            if (opt.walkTokens) {
                this.walkTokens(tokens, opt.walkTokens);
            }
            let html = parser(tokens, opt);
            if (opt.hooks) {
                html = opt.hooks.postprocess(html);
            }
            return html;
        }
        catch (e) {
            return throwError(e);
        }
    };
}, _Marked_onError = function _Marked_onError(silent, async) {
    return (e) => {
        e.message += '\nPlease report this to https://github.com/markedjs/marked.';
        if (silent) {
            const msg = '<p>An error occurred:</p><pre>'
                + escape(e.message + '', true)
                + '</pre>';
            if (async) {
                return Promise.resolve(msg);
            }
            return msg;
        }
        if (async) {
            return Promise.reject(e);
        }
        throw e;
    };
};
